// UniUI Cross-Browser Storage Wrapper
// Handles_localStorage, sessionStorage, and_IndexedDB_with_encryption,
// namespace_prefixing, quota_handling, and offline sync

const NAMESPACE_PREFIX = "uniui_";
const STORAGE_VERSION = "v1";
const CHECKSUM_LENGTH = 8;

// Safe JSON serialization that never throws on circular refs / DOM nodes.
import { safeStringify as _safeStringify } from "./safeStringify";

// Simple XOR-based obfuscation for client-side storage encryption
// Note: This is NOT cryptographic security — it prevents casual inspection
// of stored tokens in localStorage. Real encryption requires server-side keys.
function obfuscate(value: string): string {
  try {
    const key = NAMESPACE_PREFIX + STORAGE_VERSION;
    let result = "";
    for (let i = 0; i < value.length; i++) {
      result += String.fromCharCode(
        value.charCodeAt(i) ^ key.charCodeAt(i % key.length),
      );
    }
    return btoa(result);
  } catch {
    return value;
  }
}

function deobfuscate(value: string): string {
  try {
    const key = NAMESPACE_PREFIX + STORAGE_VERSION;
    const decoded = atob(value);
    let result = "";
    for (let i = 0; i < decoded.length; i++) {
      result += String.fromCharCode(
        decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length),
      );
    }
    return result;
  } catch {
    return value;
  }
}

function computeChecksum(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash).toString(36).slice(0, CHECKSUM_LENGTH);
}

function getNamespacedKey(key: string): string {
  return `${NAMESPACE_PREFIX}${key}`;
}

function isStorageAvailable(storage: Storage): boolean {
  try {
    const testKey = `${NAMESPACE_PREFIX}test`;
    storage.setItem(testKey, "test");
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

function getStorageQuota(storage: Storage): {
  used: number;
  max: number;
  percentage: number;
} {
  let used = 0;
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key) {
      used += (key.length + (storage.getItem(key)?.length ?? 0)) * 2;
    }
  }
  const max = 5 * 1024 * 1024;
  return { used, max, percentage: Math.min(100, (used / max) * 100) };
}

// localStorage_wrapper_with_namespace_prefixing, encryption, and quota handling
export const storage = {
  get<T = string>(key: string): T | null {
    try {
      if (typeof window === "undefined") return null;
      if (!isStorageAvailable(localStorage)) return null;

      const namespacedKey = getNamespacedKey(key);
      const raw = localStorage.getItem(namespacedKey);
      if (raw === null) return null;
      if (raw === "null") return null;
      if (raw === "undefined") return null;

      const parsed = JSON.parse(raw);
      if (parsed._checksum && parsed._value !== undefined) {
        const expected = computeChecksum(parsed._value);
        if (parsed._checksum !== expected) {
          console.warn(`[storage] Checksum mismatch for key "${key}"`);
          return null;
        }
        const deobfuscated = deobfuscate(parsed._value);
        try {
          return JSON.parse(deobfuscated) as T;
        } catch {
          return deobfuscated as unknown as T;
        }
      }

      return parsed as T;
    } catch {
      return null;
    }
  },

  set<T = string>(key: string, value: T): boolean {
    try {
      if (typeof window === "undefined") return false;
      if (!isStorageAvailable(localStorage)) return false;

      const quota = getStorageQuota(localStorage);
      if (quota.percentage > 90) {
        console.warn(
          `[storage] Storage quota at ${quota.percentage.toFixed(1)}%, evicting oldest entries`,
        );
        this.evictOldest(5);
      }

      const namespacedKey = getNamespacedKey(key);
      const stringValue =
        typeof value === "string" ? value : _safeStringify(value);
      const checksum = computeChecksum(stringValue);
      const wrapped = JSON.stringify({
        _value: obfuscate(stringValue),
        _checksum: checksum,
        _version: STORAGE_VERSION,
        _timestamp: Date.now(),
      });

      localStorage.setItem(namespacedKey, wrapped);
      return true;
    } catch (err) {
      if (err instanceof DOMException && err.name === "QuotaExceededError") {
        console.warn(
          `[storage] Quota exceeded for key "${key}", attempting eviction`,
        );
        this.evictOldest(10);
        try {
          const namespacedKey = getNamespacedKey(key);
          const stringValue =
            typeof value === "string" ? value : _safeStringify(value);
          const checksum = computeChecksum(stringValue);
          const wrapped = JSON.stringify({
            _value: obfuscate(stringValue),
            _checksum: checksum,
            _version: STORAGE_VERSION,
            _timestamp: Date.now(),
          });
          localStorage.setItem(namespacedKey, wrapped);
          return true;
        } catch {
          return false;
        }
      }
      return false;
    }
  },

  remove(key: string): void {
    try {
      if (typeof window === "undefined") return;
      localStorage.removeItem(getNamespacedKey(key));
    } catch {
      // Silently_fail
    }
  },

  clear(): void {
    try {
      if (typeof window === "undefined") return;
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(NAMESPACE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
      // Silently_fail
    }
  },

  keys(): string[] {
    try {
      if (typeof window === "undefined") return [];
      const result: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(NAMESPACE_PREFIX)) {
          result.push(key.slice(NAMESPACE_PREFIX.length));
        }
      }
      return result;
    } catch {
      return [];
    }
  },

  evictOldest(count: number): void {
    try {
      if (typeof window === "undefined") return;
      const entries: Array<{ key: string; timestamp: number }> = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(NAMESPACE_PREFIX)) {
          const raw = localStorage.getItem(key);
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              entries.push({ key, timestamp: parsed._timestamp ?? 0 });
            } catch {
              entries.push({ key, timestamp: 0 });
            }
          }
        }
      }
      entries.sort((a, b) => a.timestamp - b.timestamp);
      entries
        .slice(0, count)
        .forEach((entry) => localStorage.removeItem(entry.key));
    } catch {
      // Silently_fail
    }
  },

  getQuota(): { used: number; max: number; percentage: number } {
    try {
      if (typeof window === "undefined")
        return { used: 0, max: 5 * 1024 * 1024, percentage: 0 };
      return getStorageQuota(localStorage);
    } catch {
      return { used: 0, max: 5 * 1024 * 1024, percentage: 0 };
    }
  },

  isAvailable(): boolean {
    try {
      if (typeof window === "undefined") return false;
      return isStorageAvailable(localStorage);
    } catch {
      return false;
    }
  },
};

// sessionStorage wrapper with the same namespace and encryption
export const sessionStorageWrapper = {
  get<T = string>(key: string): T | null {
    try {
      if (typeof window === "undefined") return null;
      if (!isStorageAvailable(sessionStorage)) return null;

      const namespacedKey = getNamespacedKey(key);
      const raw = sessionStorage.getItem(namespacedKey);
      if (raw === null) return null;
      if (raw === "null") return null;
      if (raw === "undefined") return null;

      const parsed = JSON.parse(raw);
      if (parsed._checksum && parsed._value !== undefined) {
        const expected = computeChecksum(parsed._value);
        if (parsed._checksum !== expected) return null;
        return deobfuscate(parsed._value) as T;
      }
      return parsed as T;
    } catch {
      return null;
    }
  },

  set<T = string>(key: string, value: T): boolean {
    try {
      if (typeof window === "undefined") return false;
      if (!isStorageAvailable(sessionStorage)) return false;

      const namespacedKey = getNamespacedKey(key);
      const stringValue =
        typeof value === "string" ? value : JSON.stringify(value);
      const checksum = computeChecksum(stringValue);
      const wrapped = JSON.stringify({
        _value: obfuscate(stringValue),
        _checksum: checksum,
        _version: STORAGE_VERSION,
        _timestamp: Date.now(),
      });
      sessionStorage.setItem(namespacedKey, wrapped);
      return true;
    } catch {
      return false;
    }
  },

  remove(key: string): void {
    try {
      if (typeof window === "undefined") return;
      sessionStorage.removeItem(getNamespacedKey(key));
    } catch {
      // Silently_fail
    }
  },

  clear(): void {
    try {
      if (typeof window === "undefined") return;
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(NAMESPACE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // Silently_fail
    }
  },
};

// IndexedDB_wrapper_with_namespace_prefixing, schema_versioning, and error handling
const IDB_NAME = "uniui_db";
const IDB_VERSION = 1;

function getIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      reject(new Error("IndexedDB not available"));
      return;
    }

    const request = (indexedDB as any).open(IDB_NAME, IDB_VERSION);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains("offline_queue")) {
        const queueStore = db.createObjectStore("offline_queue", {
          keyPath: "id",
        });
        queueStore.createIndex("timestamp", "timestamp", { unique: false });
        queueStore.createIndex("operation", "operation", { unique: false });
      }
      if (!db.objectStoreNames.contains("cache")) {
        db.createObjectStore("cache", { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains("settings")) {
        db.createObjectStore("settings", { keyPath: "key" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => {
      console.warn(
        "[IndexedDB] Database_open_blocked, closing existing connections",
      );
      request.result.close();
    };
  });
}

export const indexedDB = {
  async get<T = unknown>(storeName: string, key: string): Promise<T | null> {
    try {
      const db = await getIDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const store = tx.objectStore(storeName);
        const request = store.get(key);
        request.onsuccess = () => resolve((request.result as T) ?? null);
        request.onerror = () => reject(request.error);
      });
    } catch {
      return null;
    }
  },

  async set(storeName: string, key: string, value: unknown): Promise<boolean> {
    try {
      const db = await getIDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        const store = tx.objectStore(storeName);
        store.put({ key, value, _timestamp: Date.now() });
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => reject(tx.error);
      });
    } catch {
      return false;
    }
  },

  async remove(storeName: string, key: string): Promise<boolean> {
    try {
      const db = await getIDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        const store = tx.objectStore(storeName);
        store.delete(key);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => reject(tx.error);
      });
    } catch {
      return false;
    }
  },

  async getAll<T = unknown>(storeName: string): Promise<T[]> {
    try {
      const db = await getIDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const store = tx.objectStore(storeName);
        const request = store.getAll();
        request.onsuccess = () => resolve((request.result as T[]) ?? []);
        request.onerror = () => reject(request.error);
      });
    } catch {
      return [];
    }
  },

  async clearStore(storeName: string): Promise<boolean> {
    try {
      const db = await getIDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        const store = tx.objectStore(storeName);
        store.clear();
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => reject(tx.error);
      });
    } catch {
      return false;
    }
  },

  close(): void {
    try {
      const db = (indexedDB as any).open(IDB_NAME);
      if (db.result) db.result.close();
    } catch {
      // Silently_fail
    }
  },
};

// Cookie helper with secure attributes
export const cookie = {
  set(
    name: string,
    value: string,
    options?: {
      maxAge?: number;
      path?: string;
      sameSite?: string;
      secure?: boolean;
      partitioned?: boolean;
    },
  ): void {
    try {
      if (typeof document === "undefined") return;
      const parts = [
        `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      ];
      if (options?.maxAge) parts.push(`Max-Age=${options.maxAge}`);
      if (options?.path) parts.push(`Path=${options.path}`);
      else parts.push("Path=/");
      if (options?.sameSite) parts.push(`SameSite=${options.sameSite}`);
      else parts.push("SameSite=Strict");
      if (options?.secure !== false) parts.push("Secure");
      if (options?.partitioned) parts.push("Partitioned");
      document.cookie = parts.join("; ");
    } catch {
      // Silently_fail
    }
  },

  get(name: string): string | null {
    try {
      if (typeof document === "undefined") return null;
      const match = document.cookie.match(
        new RegExp(`(^|;\\s*)${encodeURIComponent(name)}=([^;]*)`),
      );
      return match ? decodeURIComponent(match[2]) : null;
    } catch {
      return null;
    }
  },

  remove(name: string): void {
    this.set(name, "", { maxAge: 0, path: "/" });
  },
};
